import styles from "./key.module.scss";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "helpers/context";
import {
	Box, Button, IconButton, Table, TableBody, TableCell,
	TableHead, TableRow, Toolbar, TableSortLabel, Tooltip,
	TablePagination
} from "@mui/material";

import SearchInput from "./search-input/search-input";
import StatusDropdown from "./status-dropdown/status-dropdown";
import StartTimeDropdown from "./start-time-dropdown/start-time-dropdown";
import EndTimeDropdown from "./end-time-dropdown/end-time-dropdown";
import AddModal from "./add-modal/add-modal";
import QuotaModal from "./quota-modal/quota-modal"
import DeleteModal from "./delete-modal/delete-modal";
import { getApiKey, getApiClient, getApiScope } from "api/layer";

const ApiKey = () => {
	const [context, dispatch] = useContext(AppContext);
	const [t] = useTranslation();
	const [showFilters, setShowFilters] = useState(false);
	const [open, setOpen] = useState(false);
	const [openQuotaModal, setOpenQuotaModal] = useState<boolean>(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [currentInfo, setCurrentInfo] = useState<any>();
	const [searchData, setSearchData] = useState<{
		keyWord: string;
		startTime: any;
		endTime: any;
	}>({
		keyWord: "",
		startTime: null,
		endTime: null,
	});
	const [reverseSelect, setReverseSelect] = useState(false);
	const [page, setPage] = useState(0);
	const [statusDropdown, setStatusDropdown] = useState<any[]>([]);
	const [tableData, setTableData] = useState([]);
	const [clientData, setClientData] = useState([]);
	const [scopeData, setScopeData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [hoveredRow, setHoveredRow] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [sortByField, setSortByField] = useState("start_time");
	const [sortOrder, setSortOrder] = useState<{ [key: string]: "asc" | "desc" }>({
		key_code: "asc",
		client_name: "asc",
		scope: "asc",
		idaas_client_id: "asc",
		idaas_client_secrect: "asc",
		scope_names: "asc",
		reset_time: "asc",
		start_time: "asc",
		end_time: "asc",
		status: "asc",
	});

	const toggleFilters = () => {
		setShowFilters((prev) => !prev);
	};

	const clearFilters = () => {
		setShowFilters(false);
	};

	const handleStatusDropdown = (value: any) => {
		setStatusDropdown(value);
		setPage(0);
	};

	const handleSearch = (params: any, is: boolean) => {
		setSearchData((prevData) => ({ ...prevData, [params.name]: params.value }));
		if ((params.name === "startTime" || params.name === "endTime") && params.value && params.value.format("YYYY-MM-DD") === "Invalid Date") {
			return;
		}
		setReverseSelect(is);
		setPage(0);
	};

	const handleDelete = (item: any) => {
		setCurrentInfo(item);
		setOpenDeleteModal(true);
	};

	const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSave = () => {
		setOpen(false);
		initData();
	};

	const initData = async () => {
		setIsLoading(true);
		let pageParam = {
			pageNo: page + 1,
			pageSize: rowsPerPage,
			reverseSelect: reverseSelect,
			sortOrderAsc: sortOrder[sortByField] === "asc",
			sortByField: sortByField,
			...searchData
		};

		let params: any = { ...searchData };

		if (statusDropdown && statusDropdown.length > 0) {
			params.statuses = statusDropdown;
		}

		if (searchData.startTime) {
			params.startTime = searchData.startTime.format("YYYY-MM-DD");
		}

		if (searchData.endTime) {
			params.endTime = searchData.endTime.format("YYYY-MM-DD");
		}

		getApiKey(params, pageParam).then((res: any) => {
			if (res.code !== 200 || !res.data?.rows) {
				return;
			}
			setTableData(res.data.rows);
			setTotalCount(res.data.totalCount * 1 || 0);
			setIsLoading(false);
		});
	};

	const getClientData = async () => {
		getApiClient(undefined, {
			pageNo: 1,
			pageSize: 100,
		}).then((res: any) => {
			if (res.code !== 200 || !res.data?.rows) {
				return;
			}
			setClientData(res.data.rows || [])
		});
	}

	const getScopeData = async () => {
		getApiScope(undefined, {
			pageNo: 1,
			pageSize: 100,
		}).then((res: any) => {
			if (res.code !== 200 || !res.data?.rows) {
				return;
			}
			setScopeData(res.data.rows || [])
		});
	}

	const statusBgColor = (status: string) => {
		switch (status) {
			case "1":
				return "#52C41A"
			case "2":
				return "#FF4D4F"
			case "3":
				return "#999999"
			default:
				return "#999999"
		}
	}

	const statusText = (status: string) => {
		switch (status) {
			case "1":
				return "active"
			case "2":
				return "overdue"
			case "3":
				return "notActivated"
			default:
				return status
		}
	}

	useEffect(() => {
		initData();
	}, [page, rowsPerPage, searchData, reverseSelect, statusDropdown, sortByField, sortOrder]);

	useEffect(() => {
		getClientData()
		getScopeData()
	}, [])

	return (<div className={styles.container}>
		<div className={styles.title}>{t("userSystem.key")}</div>
		<Toolbar style={{ justifyContent: "space-between", borderBottom: "1px solid #ccc", marginBottom: "5px", padding: "0" }}>
			<Button
				variant="contained"
				color="success"
				size="small"
				endIcon={<span className="material-icons">add</span>}
				onClick={() => {
					setOpen(true);
					setCurrentInfo(null);
				}}
			>
				{t("userSystem.addKey")}
			</Button>

			<IconButton
				color="success"
				onClick={toggleFilters}
				style={{
					color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
					background: showFilters ? "#c1e5ca" : "",
				}}
			>
				<span className="material-icons">filter_list</span>
			</IconButton>
		</Toolbar>

		{showFilters && (
			<Box style={{ display: "flex", alignItems: "center", padding: "8px", backgroundColor: "#f5f5f5", justifyContent: "space-between" }}>
				<Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
					<SearchInput onSearch={handleSearch} />

					<StartTimeDropdown
						defaultTime={searchData.startTime}
						onSave={(value) => handleSearch({ name: "startTime", value }, reverseSelect)}
					/>
					<EndTimeDropdown
						defaultTime={searchData.endTime}
						onSave={(value) => handleSearch({ name: "endTime", value }, reverseSelect)}
					/>
					<StatusDropdown
						selectedStatus={statusDropdown}
						onSave={(value) => handleStatusDropdown(value)}
					/>
				</Box>
				<IconButton
					onClick={clearFilters}
					aria-label="clear"
					color="default"
				>
					<span className="material-icons">clear</span>
				</IconButton>
			</Box>
		)}

		<div style={{ flex: 1, overflow: "auto" }}>
			<Table
				stickyHeader
				aria-label="sticky table"
				size="small"
				style={{
					tableLayout: "fixed",
					width: "100%",
					minWidth: "100%"
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell style={{
							width: 140,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "key_code"}
								onClick={() => {
									setSortByField("key_code")
									let sort: any = {
										...sortOrder,
										key_code: sortOrder.key_code === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.key_code}
							>
								{t("userSystem.keyCode")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 120,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "client_name"}
								onClick={() => {
									setSortByField("client_name")
									let sort: any = {
										...sortOrder,
										client_name: sortOrder.client_name === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.client_name}
							>
								{t("userSystem.clientName")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 120,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "scope"}
								onClick={() => {
									setSortByField("scope")
									let sort: any = {
										...sortOrder,
										scope: sortOrder.scope === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.scope}
							>
								{t("userSystem.scope")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 140,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "idaas_client_id"}
								onClick={() => {
									setSortByField("idaas_client_id")
									let sort: any = {
										...sortOrder,
										idaas_client_id: sortOrder.idaas_client_id === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.idaas_client_id}
							>
								{t("userSystem.idaasClientId")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 120,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "idaas_client_secrect"}
								onClick={() => {
									setSortByField("idaas_client_secrect")
									let sort: any = {
										...sortOrder,
										idaas_client_secrect: sortOrder.idaas_client_secrect === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.idaas_client_secrect}
							>
								{t("userSystem.idaasClientSecrect")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 200,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "scope_names"}
								onClick={() => {
									setSortByField("scope_names")
									let sort: any = {
										...sortOrder,
										scope_names: sortOrder.scope_names === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.scope_names}
							>
								{t("userSystem.scopeId")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 150,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "reset_time"}
								onClick={() => {
									setSortByField("reset_time")
									let sort: any = {
										...sortOrder,
										reset_time: sortOrder.reset_time === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.reset_time}
							>
								{t("userSystem.resetTime")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 120,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "start_time"}
								onClick={() => {
									setSortByField("start_time")
									let sort: any = {
										...sortOrder,
										start_time: sortOrder.start_time === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.start_time}
							>
								{t("userSystem.startDate")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 120,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "end_time"}
								onClick={() => {
									setSortByField("end_time")
									let sort: any = {
										...sortOrder,
										end_time: sortOrder.end_time === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.end_time}
							>
								{t("userSystem.endDate")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 100,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "status"}
								onClick={() => {
									setSortByField("status")
									let sort: any = {
										...sortOrder,
										status: sortOrder.status === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.status}
							>
								{t("userSystem.status")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 120,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "remark"}
								onClick={() => {
									setSortByField("remark")
									let sort: any = {
										...sortOrder,
										remark: sortOrder.remark === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.remark}
							>
								{t("userSystem.remark")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{ padding: 0, width: 0 }}></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{isLoading ? (
						<TableRow>
							<TableCell colSpan={11}>
								<div className={styles.loading}>
									<div className="loading loading-spinner loading-lg"></div>
									<div>{t("layers.loading")}</div>
								</div>
							</TableCell>
						</TableRow>
					) : (
						tableData.map((item: any) => (
							<TableRow
								key={item.id}
								hover
								onMouseEnter={() => setHoveredRow(item.id)}
								onMouseLeave={() => setHoveredRow(null)}
								className={styles.row}
							>
								<TableCell style={{ wordBreak: "break-all" }}>{item.keyCode}</TableCell>
								<TableCell style={{ wordBreak: "break-all" }}>{item.clientName}</TableCell>
								<TableCell style={{ wordBreak: "break-all" }}>{item.scope}</TableCell>
								<TableCell style={{ wordBreak: "break-all" }}>{item.idaasClientId}</TableCell>
								<TableCell style={{ wordBreak: "break-all" }}>{item.idaasClientSecrect}</TableCell>
								<TableCell>
									<Tooltip
										title={item.scopeNames}
										placement="top"
										slotProps={{
											popper: {
												sx: {
													"& .MuiTooltip-tooltip": {
														wordBreak: "break-all",
													}
												}
											}
										}}
									>
										<div
											style={{
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{item.scopeNames}
										</div>
									</Tooltip>
								</TableCell>
								<TableCell>{item.resetTime}</TableCell>
								<TableCell>{item.startTime}</TableCell>
								<TableCell>{item.endTime}</TableCell>
								<TableCell><span
									className={styles.status}
									style={{ backgroundColor: statusBgColor(item.status) }}
								></span>
									{t("userSystem." + statusText(item.status))}</TableCell>
								<TableCell style={{ wordBreak: "break-all" }}>{item.remark}</TableCell>
								<TableCell style={{ padding: 0, position: "relative" }}>
									{hoveredRow === item.id && (
										<div
											className={`${styles.icons}`}
										>
											<Tooltip
												title={t("tooltip.quota")}
											>
												<IconButton
													onClick={() => {
														setCurrentInfo(item);
														setOpenQuotaModal(true);
													}}
												>
													<span className="material-icons">key</span>
												</IconButton>
											</Tooltip>

											<Tooltip
												title={t("tooltip.edit")}
											>
												<IconButton
													onClick={() => {
														setCurrentInfo(item);
														setOpen(true);
													}}
												>
													<span className="material-icons">edit</span>
												</IconButton>
											</Tooltip>

											<Tooltip
												title={t("tooltip.delete")}
											>
												<IconButton onClick={() => handleDelete(item)}>
													<span className="material-icons">delete</span>
												</IconButton>
											</Tooltip>
										</div>
									)}
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
		<div style={{ marginLeft: "auto" }}>
			<TablePagination
				component="div"
				count={totalCount}
				rowsPerPageOptions={[3, 10, 15, 25]}
				page={page}
				onPageChange={handleChangePage}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				labelRowsPerPage={t("userSystem.rowsPerPage")}
				slotProps={{
					select: {
						native: true,
					},
				}}
			/>
		</div>

		{open && (
			<AddModal
				open={open}
				onClose={() => setOpen(false)}
				onSave={handleSave}
				data={currentInfo}
				clientData={clientData}
				scopeData={scopeData}
			/>
		)}

		{openDeleteModal && (
			<DeleteModal
				data={currentInfo}
				initData={initData}
				onClose={() => setOpenDeleteModal(false)}
			/>
		)}


		{openQuotaModal && (
			<QuotaModal
				open={openQuotaModal}
				data={currentInfo}
				onClose={() => setOpenQuotaModal(false)}
			/>
		)}
	</div>)
};

export default ApiKey;
